import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Inbox } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const STATUS_STYLES = {
  active: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  verified: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  paid: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  COMPLETED: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  delivered: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  confirmed: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
  trial: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
  shipped: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20",
  pending: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  PENDING: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  payment_due: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  grace: "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20",
  suspended: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
  FAILED: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
  failed: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
  cancelled: "bg-zinc-500/10 text-zinc-500 border-zinc-500/20",
  not_connected: "bg-zinc-500/10 text-zinc-500 border-zinc-500/20",
  completed: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  pending_payment: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  refunded: "bg-zinc-500/10 text-zinc-500 border-zinc-500/20",
  paused: "bg-zinc-500/10 text-zinc-500 border-zinc-500/20",
  draft: "bg-zinc-500/10 text-zinc-500 border-zinc-500/20",
  open: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
  accepted: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  rejected: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
};

export const StatusBadge = ({ status }) => (
  <span data-testid={`status-${status}`} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}>
    {String(status || "").replace(/_/g, " ")}
  </span>
);

function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

export const StatCard = ({ icon: Icon, label, value, sub, testid, delay = 0, tint }) => {
  const numeric = typeof value === "number";
  const animated = useCountUp(numeric ? value : 0);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      data-testid={testid}
      className="relative bg-card border border-border rounded-xl p-6 overflow-hidden hover:shadow-xl hover:shadow-black/5 dark:hover:shadow-black/40 hover:-translate-y-0.5 transition-all duration-300 group">
      <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-[0.07] group-hover:opacity-[0.14] transition-opacity gradient-bg" />
      <div className="flex items-start justify-between">
        <span className="overline-label">{label}</span>
        {Icon && (
          <div className={`h-9 w-9 rounded-full flex items-center justify-center ${tint || "bg-muted"}`}>
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
      <p className="font-display font-extrabold text-3xl tracking-tight mt-3">
        {numeric ? animated.toLocaleString("en-IN") : value}
      </p>
      {sub && <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">{sub}</p>}
    </motion.div>
  );
};

export const PageHeader = ({ title, subtitle, children, testid }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <div>
      <h1 data-testid={testid || "page-title"} className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter">{title}</h1>
      {subtitle && <p className="text-muted-foreground mt-2 text-base">{subtitle}</p>}
    </div>
    <div className="flex items-center gap-3">{children}</div>
  </div>
);

export const EmptyState = ({ title, body, children, icon: Icon = Inbox }) => (
  <div data-testid="empty-state" className="border border-dashed border-border rounded-xl py-16 px-8 flex flex-col items-center text-center gradient-bg-soft">
    <div className="h-14 w-14 rounded-full bg-card border border-border flex items-center justify-center mb-4 glow-sm">
      <Icon className="h-6 w-6 text-muted-foreground" />
    </div>
    <p className="font-display font-bold text-lg">{title}</p>
    <p className="text-sm text-muted-foreground mt-1 max-w-sm">{body}</p>
    {children && <div className="mt-6">{children}</div>}
  </div>
);

export const TableShell = ({ head, children, testid }) => (
  <div data-testid={testid} className="bg-card border border-border rounded-xl overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            {head.map((h) => <th key={h} className="text-left px-5 py-3.5 overline-label whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  </div>
);

export const TR = ({ children, onClick, testid }) => (
  <tr data-testid={testid} onClick={onClick} className={`border-b border-border/50 last:border-0 hover:bg-muted/50 transition-colors ${onClick ? "cursor-pointer" : ""}`}>
    {children}
  </tr>
);

export const TD = ({ children, className = "" }) => <td className={`px-5 py-4 align-middle ${className}`}>{children}</td>;

export const LoadingRows = ({ rows = 4 }) => (
  <div className="space-y-3">{Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>
);

export const ConfirmDialog = ({ trigger, title, description, onConfirm, testid }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display">{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testid}-cancel`}>Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid={`${testid}-confirm`} onClick={onConfirm}
          className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Confirm</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const TrendUp = ({ children }) => (
  <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold">
    <ArrowUpRight className="h-3 w-3" />{children}
  </span>
);

export const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay, ease: "easeOut" }}>
    {children}
  </motion.div>
);

export const Marquee = ({ items }) => (
  <div className="overflow-hidden whitespace-nowrap py-5 border-y border-border/60 select-none">
    <div className="inline-flex animate-marquee">
      {[...items, ...items, ...items].map((t, i) => (
        <span key={i} className="font-display font-extrabold text-sm tracking-[0.35em] uppercase text-muted-foreground/60 mx-8">{t}</span>
      ))}
    </div>
  </div>
);
